import { FUEL_DENSITY } from "./units";

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;
export const norm360 = (a) => {
  let x = a % 360;
  if (x < 0) x += 360;
  return x;
};

// Compute a single leg wind triangle.
// variation: déclinaison magnétique, convention Ouest positif / Est négatif.
// deviation: déviation compas (carte de déviation).
export function computeLeg(leg, tas) {
  const trackTrue = Number(leg.trackTrue) || 0;
  const distance = Number(leg.distance) || 0;
  const variation = Number(leg.variation) || 0;
  const deviation = Number(leg.deviation) || 0;
  const windDir = Number(leg.windDir) || 0;
  const windSpeed = Number(leg.windSpeed) || 0;
  const t = Number(tas) || 0;

  let wca = 0;
  let gs = t;
  const wtAngle = toRad(windDir - trackTrue);
  if (t > 0) {
    let sinWca = (windSpeed * Math.sin(wtAngle)) / t;
    sinWca = Math.max(-1, Math.min(1, sinWca));
    wca = toDeg(Math.asin(sinWca));
    gs = t * Math.cos(toRad(wca)) - windSpeed * Math.cos(wtAngle);
  }
  if (gs < 0) gs = 0;

  const headingTrue = norm360(trackTrue + wca);
  const headingMag = norm360(headingTrue + variation);
  const headingCompass = norm360(headingMag + deviation);
  const timeMin = gs > 0 ? (distance / gs) * 60 : 0;

  return {
    wca: Math.round(wca * 10) / 10,
    gs: Math.round(gs),
    trackMag: norm360(trackTrue + variation),
    headingTrue: Math.round(headingTrue),
    headingMag: Math.round(headingMag),
    headingCompass: Math.round(headingCompass),
    timeMin,
    distance,
  };
}

// Compute full nav log. Returns per-leg rows with cumulative totals.
export function computeNavLog(legs, aircraft) {
  const tas = Number(aircraft?.tasCruise) || 0;
  const burn = Number(aircraft?.fuelBurn) || 0;
  let cumTime = 0;
  let cumFuel = 0;
  const rows = (legs || []).map((leg) => {
    const r = computeLeg(leg, tas);
    const fuel = (r.timeMin / 60) * burn;
    cumTime += r.timeMin;
    cumFuel += fuel;
    return { ...leg, ...r, fuel, cumTime, cumFuel };
  });
  return {
    rows,
    totalDistance: rows.reduce((s, r) => s + (Number(r.distance) || 0), 0),
    totalTime: cumTime,
    totalFuel: cumFuel,
  };
}

// Aller (+ retour si route A/R). nav.rows = aller ; totaux = cumul aller + retour.
export function computeBriefingNav(briefing, aircraft) {
  const out = computeNavLog(briefing?.legs || [], aircraft);
  const ret = briefing?.roundTrip ? computeNavLog(briefing?.returnLegs || [], aircraft) : null;
  const sum = (k) => out[k] + (ret ? ret[k] : 0);
  return { ...out, out, ret, totalDistance: sum("totalDistance"), totalTime: sum("totalTime"), totalFuel: sum("totalFuel") };
}

const num = (v, d) => (v === undefined || v === null || v === "" ? d : Number(v) || 0);
export const FREE_DEFAULTS = { contingency: { v: "", unit: "min" }, alternate: { v: 30, unit: "min" }, cdb: { v: "", unit: "min" } };

// Litres d'un forfait roulage (un seul roulage, utilisé pour le centrage).
export const taxiFuelL = (fuel, burn) => (num(fuel?.taxiStd, num(fuel?.taxiMin, 10)) / 60) * (Number(burn) || 0);

// Bilan carburant. Saisies libres stockées { v, unit: "min"|"L" } et converties via la conso (L/h).
export function computeFuelPlan(fuel, { trip = 0, burn = 0, unusable = 0, onboard = 0 } = {}) {
  const f = fuel || {};
  const b = Number(burn) || 0;
  const toL = (m) => (m / 60) * b;
  const toMin = (l) => (b > 0 ? (l / b) * 60 : 0);
  const free = (key) => {
    const e = f[key] && typeof f[key] === "object" ? f[key] : FREE_DEFAULTS[key];
    if (e.v === "" || e.v === undefined) return { min: 0, L: 0, entry: e };
    const v = Number(e.v) || 0;
    return e.unit === "L" ? { min: toMin(v), L: v, entry: e } : { min: v, L: toL(v), entry: e };
  };
  const counted = (key, std) => {
    const count = num(f[`${key}Count`], 1);
    const s = num(f[`${key}Std`], std);
    return { count, std: s, min: count * s, L: toL(count * s) };
  };
  const night = !!f.night;
  const reserveMin = night ? 45 : 30;
  const rows = [
    { key: "contingency", label: "Marge aléas", kind: "free", ...free("contingency") },
    { key: "taxi", label: "Forfait roulage", kind: "count", ...counted("taxi", num(f.taxiMin, 10)) },
    { key: "procedure", label: "Forfait procédure", kind: "count", ...counted("procedure", 10) },
    { key: "reserve", label: "Réserve réglementaire VFR", kind: "reserve", std: reserveMin, night, min: reserveMin, L: toL(reserveMin) },
    { key: "unusable", label: "Carburant non utilisable (manuel de vol)", kind: "fixed", min: toMin(Number(unusable) || 0), L: Number(unusable) || 0 },
    { key: "alternate", label: "Alternative déroutement", kind: "free", ...free("alternate") },
    { key: "cdb", label: "Réserve exploitant CDB", kind: "free", ...free("cdb") },
  ];
  const by = Object.fromEntries(rows.map((r) => [r.key, r.L]));
  const tripL = Number(trip) || 0;
  const minRequired = by.taxi + tripL + by.contingency + by.procedure + by.alternate + by.reserve + by.unusable;
  const totalRequired = minRequired + by.cdb;
  const ob = Number(onboard) || 0;
  const margin = ob - totalRequired;
  return {
    rows, trip: tripL, taxi: by.taxi, contingency: by.contingency, procedure: by.procedure, alternate: by.alternate,
    reserve: by.reserve, unusable: by.unusable, cdb: by.cdb, minRequired, totalRequired, onboard: ob, margin,
    endurance: toMin(ob), marginTime: toMin(margin),
  };
}

// Un bilan global, ou deux (aller / retour) si l'utilisateur sépare un A/R.
export function fuelPlans(briefing, nav, aircraft) {
  const fuel = briefing?.fuel || {};
  const base = { burn: Number(aircraft?.fuelBurn) || 0, unusable: Number(aircraft?.unusableFuelL) || 0 };
  if (nav.ret && fuel.splitRoundTrip) {
    return [
      { title: "Aller", onboardKey: "onboardL", plan: computeFuelPlan(fuel, { ...base, trip: nav.out.totalFuel, onboard: fuel.onboardL }) },
      { title: "Retour", onboardKey: "onboardRetL", plan: computeFuelPlan(fuel, { ...base, trip: nav.ret.totalFuel, onboard: fuel.onboardRetL }) },
    ];
  }
  return [{ title: nav.ret ? "Aller + Retour" : "", onboardKey: "onboardL", plan: computeFuelPlan(fuel, { ...base, trip: nav.totalFuel, onboard: fuel.onboardL }) }];
}

// Weight & Balance.
// aircraft.emptyWeight (kg), emptyArm (m), stations[{name, arm}], fuelArm, envelope[{cg, weight}]
// weights: { [stationName]: kg }, fuelStartL
// Masse/centrage effectif : siège avant = pilote + passager avant.
export function wbEffective(briefing, aircraft, nav) {
  const stations = aircraft?.stations || [];
  const firstStation = stations[0]?.name;
  const front = briefing.frontSeat || {};
  const frontTotal = (Number(front.pilot ?? briefing.pilotWeight ?? 0) || 0) + (Number(front.pax ?? 0) || 0);
  const weights = { ...(briefing.weights || {}), ...(firstStation ? { [firstStation]: frontTotal } : {}) };
  return computeWB(aircraft || {}, weights, Number(briefing.fuelStartL) || 0, taxiFuelL(briefing.fuel, aircraft?.fuelBurn), nav.totalFuel);
}

// Points du graphique de centrage : décollage, carburant mini (non utilisable seul), atterrissage(s).
export function wbChartData(wb, aircraft, briefing, nav) {
  const envelope = (aircraft?.envelope || []).map((p) => ({ cg: Number(p.cg), weight: Number(p.weight), label: "Enveloppe" }));
  const taxi = taxiFuelL(briefing.fuel, aircraft?.fuelBurn);
  const toFuel = (Number(briefing.fuelStartL) || 0) - taxi;
  const pt = (label, fuelL) => ({ ...wb.atFuel(Math.max(0, fuelL)), label });
  const takeoff = { cg: wb.takeoff.cg, weight: wb.takeoff.weight, label: "Décollage" };
  const minFuel = pt("Carburant mini (non utilisable seul)", Number(aircraft?.unusableFuelL) || 0);
  const mode = nav.ret ? briefing.wbLandingMode || "both" : "dest";
  const landings = [];
  if (mode !== "return") landings.push(pt(nav.ret ? "Atterrissage destination" : "Atterrissage", toFuel - nav.out.totalFuel));
  if (nav.ret && mode !== "dest") landings.push(pt("Atterrissage retour", toFuel - nav.out.totalFuel - taxi - nav.ret.totalFuel));
  return { envelope, takeoff, minFuel, landings, mode };
}

export function computeWB(aircraft, weights, fuelStartL, taxiFuelL, tripFuelL) {
  const emptyWeight = Number(aircraft?.emptyWeight) || 0;
  const emptyArm = Number(aircraft?.emptyArm) || 0;
  const fuelArm = Number(aircraft?.fuelArm) || 0;
  const stations = aircraft?.stations || [];

  const items = [];
  items.push({ name: "Masse à vide", weight: emptyWeight, arm: emptyArm, moment: emptyWeight * emptyArm });
  stations.forEach((s) => {
    const w = Number(weights?.[s.name]) || 0;
    items.push({ name: s.name, weight: w, arm: Number(s.arm) || 0, moment: w * (Number(s.arm) || 0) });
  });

  const fuelWeight = (Number(fuelStartL) || 0) * FUEL_DENSITY;
  const nonFuelWeight = items.reduce((s, i) => s + i.weight, 0);
  const nonFuelMoment = items.reduce((s, i) => s + i.moment, 0);

  const rampWeight = nonFuelWeight + fuelWeight;
  const rampMoment = nonFuelMoment + fuelWeight * fuelArm;
  const rampCG = rampWeight > 0 ? rampMoment / rampWeight : 0;

  const toFuelWeight = fuelWeight - (Number(taxiFuelL) || 0) * FUEL_DENSITY;
  const toWeight = nonFuelWeight + toFuelWeight;
  const toMoment = nonFuelMoment + toFuelWeight * fuelArm;
  const toCG = toWeight > 0 ? toMoment / toWeight : 0;

  const ldgFuelWeight = toFuelWeight - (Number(tripFuelL) || 0) * FUEL_DENSITY;
  const ldgWeight = nonFuelWeight + ldgFuelWeight;
  const ldgMoment = nonFuelMoment + ldgFuelWeight * fuelArm;
  const ldgCG = ldgWeight > 0 ? ldgMoment / ldgWeight : 0;

  const mtow = Number(aircraft?.mtow) || 0;
  const envelope = aircraft?.envelope || [];

  return {
    atFuel: (L) => {
      const fw = (Number(L) || 0) * FUEL_DENSITY;
      const w = nonFuelWeight + fw;
      return { weight: w, cg: w > 0 ? (nonFuelMoment + fw * fuelArm) / w : 0 };
    },
    items: [...items, { name: "Carburant", weight: fuelWeight, arm: fuelArm, moment: fuelWeight * fuelArm }],
    fuelWeight,
    ramp: { weight: rampWeight, cg: rampCG, ok: inEnvelope(envelope, rampCG, rampWeight) && rampWeight <= mtow },
    takeoff: { weight: toWeight, cg: toCG, ok: inEnvelope(envelope, toCG, toWeight) && toWeight <= mtow },
    landing: { weight: ldgWeight, cg: ldgCG, ok: inEnvelope(envelope, ldgCG, ldgWeight) },
    mtow,
    overMtow: toWeight > mtow,
  };
}

export function inEnvelope(envelope, cg, weight) {
  if (!envelope || envelope.length < 3) return true; // no envelope defined -> don't flag
  let inside = false;
  const n = envelope.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = Number(envelope[i].cg), yi = Number(envelope[i].weight);
    const xj = Number(envelope[j].cg), yj = Number(envelope[j].weight);
    const intersect =
      (yi > weight) !== (yj > weight) &&
      cg < ((xj - xi) * (weight - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
